import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, Input, Button, Spinner } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaPencilAlt, FaFilter } from "react-icons/fa";
import { ImDownload } from "react-icons/im";

// ---- all helpers & APIs live in ../../import per your note ----
import { _Get, _GetList, _Delete } from "../../import/ApiEndPoint/ApiCalling";
import { CheckPermission } from "../../import/CheckPermission";
import {
  SubscriptinPlanList,
  Delete_SubscriptinPlan,
} from "../../import/ApiEndPoint/Api";
import {
  exportDataToPDF,
  exportDataToExcel,
  convertDataCSVtoExcel,
  convertDataCsvToXml,
} from "../../import/Downloader";

const injectOnce = () => {
  if (document.getElementById("planlist-inline-css")) return;
  const s = document.createElement("style");
  s.id = "planlist-inline-css";
  s.textContent = `
    .pl-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px;
    }
    .pl-card{ border-radius:14px; border:1px solid #e5e7eb; }
    .pl-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .pl-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:900px; }
    .pl-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; text-align:center; white-space:nowrap;
    }
    .pl-table tbody td{
      padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center;
    }
    .pl-table tbody tr:hover{ background:#f9fbff; }
    .actions{ display:flex; justify-content:center; align-items:center; gap:8px }
    .chip{ display:inline-grid; place-items:center; width:28px; height:28px; border-radius:16px; }
    .chip-edit{ background:#d46f10; }
    .chip-del{ background:#ec1809; }
    .tag{ display:inline-block; background:#eef2ff; color:#1f2937; border:1px solid #e5e7eb; padding:2px 8px; border-radius:999px; font-size:12px; margin:2px; }
  `;
  document.head.appendChild(s);
};

const toStr = (v) => (v === null || v === undefined ? "" : String(v));

const getGroupNames = (groups) => {
  if (!Array.isArray(groups) || !groups.length) return [];
  // populated objects → prefer `groupName` then `name` then `_id`
  return groups.map((g) => g?.groupName || g?.name || g?._id || String(g));
};

export default function SubscriptionPlanList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [search, setSearch] = useState("");
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [permissions, setPermissions] = useState({
    View: true,
    Create: true,
    Edit: true,
    Delete: true,
    Download: true,
  });

  useEffect(() => injectOnce(), []);

  const reload = async () => {
    const userinfo = JSON.parse(localStorage.getItem("userData") || "{}");
    const db = userinfo?.database || userinfo?.db || "";

    // permissions
    const inside = CheckPermission("Subscription Plan");
    if (inside && typeof inside === "object") {
      setPermissions((prev) => ({ ...prev, ...inside }));
    }

    // fetch
    const res = await _GetList(SubscriptinPlanList, db);
    const list = Array.isArray(res?.Subscription) ? res.Subscription : [];
    setRows(list);
    setAllRows(list);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await reload();
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) => {
      const groups = getGroupNames(r?.groups).join(", ");
      return [
        r?.planName,
        r?.subscriptionType === 1 ? "one time" : "yearly",
        r?.subscriptionCost,
        r?.days,
        r?.noOfUser,
        r?.annualMaintenanceCost,
        groups,
      ]
        .map(toStr)
        .some((x) => x.toLowerCase().includes(term));
    });
  }, [rows, search]);

  const handleDelete = async (id) => {
    if (!permissions?.Delete) return;
    if (!window.confirm("Sure you want to delete this plan?")) return;
    try {
      await _Delete(Delete_SubscriptinPlan, id);
      setRows((prev) => prev.filter((x) => x?._id !== id));
      setAllRows((prev) => prev.filter((x) => x?._id !== id));
    } catch (e) {
      // ignore
    }
  };

  const handleExportPDF = async () => {
    const csv = buildCsv(filtered);
    await exportDataToPDF(csv, "SubscriptionPlans");
  };
  const handleExportXLSX = async () => {
    const csv = buildCsv(filtered);
    await exportDataToExcel(csv, "SubscriptionPlans");
  };
  const handleExportXLS = async () => {
    const csv = buildCsv(filtered);
    await convertDataCSVtoExcel(csv, "SubscriptionPlans");
  };
  const handleExportXML = async () => {
    const csv = buildCsv(filtered);
    await convertDataCsvToXml(csv, "SubscriptionPlans");
  };

  const buildCsv = (data) => {
    const headers = [
      "Plan Name",
      "Type",
      "Price",
      "Days",
      "Users",
      "AMC",
      "Groups",
    ];
    const lines = data.map((r) => {
      const groups = getGroupNames(r?.groups).join("; ");
      const cells = [
        r?.planName || "",
        r?.subscriptionType === 1 ? "One Time" : "Yearly",
        r?.subscriptionCost || "",
        r?.days || "",
        r?.noOfUser || "",
        r?.annualMaintenanceCost || "",
        groups,
      ].map((v) => `"${toStr(v).replace(/"/g, '""')}"`);
      return cells.join(",");
    });
    return [headers.join(","), ...lines].join("\r\n");
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  return (
    <>
      {/* ===== Toolbar ===== */}
      <Row className="pl-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            className="pl-card"
            style={{ padding: 8, background: "#f4f7f6" }}
          >
            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Subscription Plan List{" "}
                <span className="count-pill">{filtered.length}</span>
              </div>

              <div style={{ flex: "1 1 260px" }} />

              <div
                style={{
                  minWidth: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Search plans..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              {permissions?.Create && (
                <Button
                  style={{
                    backgroundColor: "#39cccc",
                    color: "white",
                    fontWeight: 600,
                    height: 32,
                    textTransform: "uppercase",
                  }}
                  onClick={() =>
                    navigate("/app/master/subscription-plans/create/0")
                  }
                >
                  <FaPlus size={12} /> Plan
                </Button>
              )}

              {permissions?.Download && (
                <div
                  onMouseEnter={() => setDownloadOpen(true)}
                  onMouseLeave={() => setDownloadOpen(false)}
                  style={{ position: "relative" }}
                >
                  <ImDownload
                    style={{ cursor: "pointer" }}
                    title="download"
                    size={24}
                    color="#39cccc"
                  />
                  {downloadOpen && (
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: 26,
                        zIndex: 5,
                        border: "1px solid #39cccc",
                        backgroundColor: "white",
                        borderRadius: 10,
                        overflow: "hidden",
                        minWidth: 130,
                        boxShadow: "0 12px 24px rgba(0,0,0,.12)",
                      }}
                    >
                      <div style={dlItem} onClick={handleExportPDF}>
                        .PDF
                      </div>
                      <div style={dlItem} onClick={() => handleExportXLSX()}>
                        .XLSX
                      </div>
                      <div style={dlItem} onClick={() => handleExportXLS()}>
                        .XLS
                      </div>
                      <div style={dlItem} onClick={() => handleExportXML()}>
                        .XML
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* ===== Table ===== */}
      <Row>
        <Col sm="12">
          <Card className="pl-card" style={{ padding: 8 }}>
            {filtered.length === 0 ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No plans found.
              </div>
            ) : (
              <div className="pl-table-wrap">
                <table className="pl-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Plan Name</th>
                      <th>Type</th>
                      <th>Price</th>
                      <th>Days</th>
                      <th>Users</th>
                      <th>AMC</th>
                      <th>Groups</th>
                      <th>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r, i) => {
                      const groupNames = getGroupNames(r?.groups);
                      return (
                        <tr key={r?._id || r?.id || i}>
                          <td>{i + 1}</td>
                          <td>{r?.planName || "-"}</td>
                          <td>
                            {r?.subscriptionType === 1 ? "One Time" : "Yearly"}
                          </td>
                          <td>{toStr(r?.subscriptionCost)}</td>
                          <td>{toStr(r?.days)}</td>
                          <td>{toStr(r?.noOfUser)}</td>
                          <td>{r?.annualMaintenanceCost ?? "NA"}</td>
                          <td style={{ textAlign: "left" }}>
                            {groupNames.length ? (
                              groupNames.map((g, idx) => (
                                <span key={idx} className="tag">
                                  {g}
                                </span>
                              ))
                            ) : (
                              <span style={{ color: "#6b7280" }}>—</span>
                            )}
                          </td>
                          <td>
                            <div className="actions">
                              {permissions?.Edit && (
                                <span
                                  className="chip chip-edit"
                                  title="Edit"
                                  onClick={() =>
                                    navigate(
                                      `/app/master/subscription-plans/create/${r?._id}`
                                    )
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  <FaPencilAlt size={14} color="white" />
                                </span>
                              )}
                              {permissions?.Delete && (
                                <span
                                  className="chip chip-del"
                                  title="Delete"
                                  onClick={() => handleDelete(r?._id)}
                                  style={{ cursor: "pointer" }}
                                >
                                  {/* simple X glyph to keep deps minimal */}
                                  <span
                                    style={{ color: "white", fontWeight: 800 }}
                                  >
                                    ×
                                  </span>
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </>
  );
}

const dlItem = {
  padding: "10px 12px",
  cursor: "pointer",
  fontWeight: 600,
  letterSpacing: ".02em",
};
